import type { Crumb } from "@/components/ui/Breadcrumb";
import { langPath, type Dict, type Lang } from "@/lib/i18n";

export type ListingType = "courses" | "events" | "otts" | "podcasts";

/**
 * Breadcrumb trail for a module detail page, surface-aware.
 *
 * In the in-app (library) surface the trail roots at the dashboard and the
 * listing crumb points at /library/<type>; on the public marketing surface it
 * roots at Home and the bare /<type>. Keeps in-app detail navigation fully
 * client-side — a public-URL breadcrumb would otherwise bounce a logged-in
 * user through the proxy redirect and break the SPA feel.
 *
 * `t` is the caller's RESOLVED Dict (useDict()/getDict()) — this helper must
 * not call raw dict() itself: that bypasses the admin-translations overlay,
 * and since D19 the baked Dict is English-only, so a raw read would render
 * English crumbs on every non-EN page.
 */
export function detailCrumbs(
  t: Dict,
  lang: Lang,
  type: ListingType,
  name: string | null | undefined,
  library: boolean,
): Crumb[] {
  const listingLabel = {
    courses: t.listing.coursesTitle,
    events: t.listing.eventsTitle,
    otts: t.listing.ottsTitle,
    podcasts: t.listing.podcastsTitle,
  }[type];
  const root: Crumb = library
    ? { href: langPath(lang, "/dashboard"), label: t.appNav.dashboard }
    : { href: langPath(lang, "/"), label: t.nav.home };
  const listing: Crumb = {
    href: langPath(lang, `${library ? "/library" : ""}/${type}`),
    label: listingLabel,
  };
  return [root, listing, { label: name ?? "—" }];
}
